import React, { useState } from 'react';
import { Users, Award, BookOpen, Network, TrendingUp, Brain, Upload, Send, CheckCircle, Mail, Phone, MessageCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const CareersSection = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    collegeName: '',
    courseBranch: '',
    yearOfStudy: '',
    positionApplying: '',
    whyJoin: '',
    resume: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const benefits = [
    {
      icon: Award,
      title: "Certificate",
      description: "Official completion certificate",
      color: "from-[#5f9dff] to-[#4a8ae8]"
    },
    {
      icon: BookOpen,
      title: "Letter of Recommendation",
      description: "Professional reference letter",
      color: "from-[#61525a] to-[#4a3f47]"
    },
    {
      icon: Users,
      title: "Internship Experience",
      description: "Real-world project experience",
      color: "from-[#ff8c19] to-[#e67c15]"
    },
    {
      icon: Network,
      title: "Networking Opportunities",
      description: "Connect with industry professionals",
      color: "from-[#b4dc19] to-[#9bc216]"
    },
    {
      icon: TrendingUp,
      title: "Performance Incentives",
      description: "Rewards for outstanding performance",
      color: "from-[#c8aff0] to-[#b799e6]"
    },
    {
      icon: Brain,
      title: "Learning AI & Business Skills",
      description: "Hands-on experience with latest technologies",
      color: "from-[#3dd3ee] to-[#2bc4db]"
    }
  ];

  const positions = [
    "Campus Ambassadors",
    "Marketing Interns",
    "Business Development Interns", 
    "Research Interns",
    "Operations Interns"
  ];

  const yearOptions = [
    "1st Year",
    "2nd Year", 
    "3rd Year",
    "4th Year",
    "Final Year",
    "Post Graduate"
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'application/pdf' || file.type === 'application/msword' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
      setFormData(prev => ({ ...prev, resume: file }));
    } else {
      alert('Please upload a PDF, DOC, or DOCX file.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      console.log('Form submitted:', formData);
      setIsSubmitted(true);
      setIsSubmitting(false);
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          collegeName: '',
          courseBranch: '',
          yearOfStudy: '',
          positionApplying: '',
          whyJoin: '',
          resume: null
        });
      }, 5000);
    }, 2000);
  };

  const isFormValid = () => {
    return formData.fullName && formData.email && formData.phone && 
           formData.yearOfStudy && formData.positionApplying && formData.whyJoin;
  };

  if (isSubmitted) {
    return (
      <section id="careers" className="py-20 bg-gradient-to-br from-[#5f9dff]/5 to-[#61525a]/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-xl">
            <CardContent className="p-12 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-[#1e1919] mb-4 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
                Application Submitted Successfully!
              </h3>
              <p className="text-lg text-[#736c64] mb-6">
                Thank you for applying to Knowledge Ninja Labs. Our team will contact you shortly.
              </p>
              <Button 
                onClick={() => window.location.reload()}
                className="bg-[#61525a] hover:bg-[#4a3f47] text-white px-8 py-3 rounded-xl font-semibold"
              >
                Submit Another Application
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section id="careers" className="py-20 bg-gradient-to-br from-white via-[#f7f5f2] to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-gradient-to-r from-[#5f9dff]/10 to-[#61525a]/10 rounded-full px-4 py-2 mb-6">
            <Users className="w-5 h-5 text-[#5f9dff] mr-2" />
            <span className="text-sm font-medium text-[#61525a]">Join Our Team</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e1919] mb-6 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
            We're Hiring — 
            <span className="bg-gradient-to-r from-[#5f9dff] to-[#61525a] bg-clip-text text-transparent"> Interns & Student Brand Ambassadors</span>
          </h2>
          <p className="text-lg text-[#736c64] max-w-4xl mx-auto leading-relaxed">
            Knowledge Ninja Labs is hiring passionate students for exciting opportunities to gain real-world experience, 
            build professional networks, and develop cutting-edge skills in AI and business intelligence.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Positions & Benefits */}
          <div>
            {/* Positions */}
            <Card className="mb-8 bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-[#1e1919] font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
                  Open Positions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {positions.map((position, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-gradient-to-r from-[#5f9dff]/5 to-[#61525a]/5 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#5f9dff] to-[#61525a] flex items-center justify-center">
                        <span className="text-white font-bold text-sm">{index + 1}</span>
                      </div>
                      <span className="font-medium text-[#1e1919]">{position}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Benefits */}
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-[#1e1919] font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
                  Benefits & Perks
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {benefits.map((benefit, index) => {
                    const IconComponent = benefit.icon;
                    return (
                      <div key={index} className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${benefit.color} flex items-center justify-center flex-shrink-0`}>
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-[#1e1919] text-sm">{benefit.title}</h4>
                          <p className="text-xs text-[#736c64]">{benefit.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* CTA Buttons */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              <Button
                onClick={() => window.open('https://wa.me/message/RWI6VRIA4IHIJ1', '_blank')}
                className="bg-gradient-to-r from-[#25d366] to-[#128c7e] hover:from-[#1ea952] hover:to-[#0e6b5c] text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp Us
              </Button>
              <Button
                onClick={() => window.open('tel:+919266849651', '_self')}
                className="bg-gradient-to-r from-[#61525a] to-[#4a3f47] hover:from-[#4a3f47] hover:to-[#61525a] text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Us
              </Button>
            </div>
          </div>

          {/* Right Column - Application Form */}
          <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-[#1e1919] font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
                Apply Now
              </CardTitle>
              <p className="text-[#736c64]">Fill out the form below to start your journey with us</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Full Name */}
                <div>
                  <Label htmlFor="fullName" className="text-[#61525a] font-semibold mb-2 block">
                    Full Name *
                  </Label>
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    className="h-12"
                    required
                  />
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email" className="text-[#61525a] font-semibold mb-2 block">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-[#61525a] font-semibold mb-2 block">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+91 XXXXXXXXXX"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="h-12"
                      required
                    />
                  </div>
                </div>

                {/* College & Course */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="collegeName" className="text-[#61525a] font-semibold mb-2 block">
                      College Name
                    </Label>
                    <Input
                      id="collegeName"
                      type="text"
                      placeholder="Your college/university name"
                      value={formData.collegeName}
                      onChange={(e) => handleInputChange('collegeName', e.target.value)}
                      className="h-12"
                    />
                  </div>
                  <div>
                    <Label htmlFor="courseBranch" className="text-[#61525a] font-semibold mb-2 block">
                      Course / Branch
                    </Label>
                    <Input
                      id="courseBranch"
                      type="text"
                      placeholder="e.g., B.Tech CSE, MBA, etc."
                      value={formData.courseBranch}
                      onChange={(e) => handleInputChange('courseBranch', e.target.value)}
                      className="h-12"
                    />
                  </div>
                </div>

                {/* Year & Position */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#61525a] font-semibold mb-2 block">
                      Year of Study *
                    </Label>
                    <Select value={formData.yearOfStudy} onValueChange={(value) => handleInputChange('yearOfStudy', value)}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select your year" />
                      </SelectTrigger>
                      <SelectContent>
                        {yearOptions.map((year) => (
                          <SelectItem key={year} value={year}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#61525a] font-semibold mb-2 block">
                      Position Applying For *
                    </Label>
                    <Select value={formData.positionApplying} onValueChange={(value) => handleInputChange('positionApplying', value)}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select position" />
                      </SelectTrigger>
                      <SelectContent>
                        {positions.map((position) => (
                          <SelectItem key={position} value={position}>
                            {position}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Why Join */}
                <div>
                  <Label htmlFor="whyJoin" className="text-[#61525a] font-semibold mb-2 block">
                    Why do you want to join? *
                  </Label>
                  <Textarea
                    id="whyJoin"
                    placeholder="Tell us about your motivation, goals, and what you hope to achieve..."
                    value={formData.whyJoin}
                    onChange={(e) => handleInputChange('whyJoin', e.target.value)}
                    className="min-h-[120px] resize-none"
                    required
                  />
                </div>

                {/* Resume Upload */}
                <div>
                  <Label htmlFor="resume" className="text-[#61525a] font-semibold mb-2 block">
                    Resume Upload (PDF/DOC/DOCX)
                  </Label>
                  <div className="relative">
                    <Input
                      id="resume"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      className="h-12 cursor-pointer"
                    />
                    <Upload className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                  {formData.resume && (
                    <p className="text-sm text-green-600 mt-1">✓ {formData.resume.name}</p>
                  )}
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit" 
                  disabled={!isFormValid() || isSubmitting}
                  className="w-full bg-gradient-to-r from-[#5f9dff] to-[#61525a] hover:from-[#4a8ae8] hover:to-[#4a3f47] text-white h-12 text-lg font-semibold transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                      Submitting Application...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Send className="w-5 h-5 mr-2" />
                      Apply Now
                    </div>
                  )}
                </Button>

                <div className="flex items-center justify-center space-x-4 mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => window.open('mailto:info@knowledgeninjalabs.com', '_blank')}
                    className="border-[#5f9dff] text-[#5f9dff] hover:bg-[#5f9dff] hover:text-white px-6 py-2"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email Us
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export { CareersSection };